import { uniq } from 'ramda';
import { ParsedHomeQueryParams } from '../parsedQueryParams.ts';
import { AddAccountParams, MultiAccountLogic } from './logic.ts';
import { addDays } from '../utils/dates.ts';
import { Account } from './data.ts';
import { findEvent } from '../../../../packages/src/api/index.js';
import { initLastUpdateDateStorage, initThrottleByDays } from '../utils/throttle.ts';

export async function selectCurrentAccount({
  parsedQueryParams,
  fetchAccountData,
  multiAccounts,
}: {
  parsedQueryParams: ParsedHomeQueryParams;
  multiAccounts: MultiAccountLogic;
  fetchAccountData: (homeQueryParams: ParsedHomeQueryParams) => Promise<AddAccountParams | void>;
}) {
  if (parsedQueryParams.eventCode && parsedQueryParams.attendeeUid) {
    if (
      multiAccounts.isExistsAccount({
        attendeeUid: parsedQueryParams.attendeeUid,
        eventCode: parsedQueryParams.eventCode,
      })
    ) {
      multiAccounts.setCurrentAccount(
        multiAccounts.findIndex({
          attendeeUid: parsedQueryParams.attendeeUid,
          eventCode: parsedQueryParams.eventCode,
        })
      );
    } else {
      const accountData = await fetchAccountData(parsedQueryParams);

      if (accountData) {
        multiAccounts.addAccount(accountData);
        multiAccounts.setCurrentAccount(0);
      }
    }
  } else if (parsedQueryParams.eventCode && !parsedQueryParams.attendeeUid) {
    const index = multiAccounts.findIndexByEventCode(parsedQueryParams.eventCode);
    multiAccounts.setCurrentAccount(index);
  }
}

type fetchEventDataType = ({
  eventName,
}: {
  eventName: string;
}) => Promise<{ result: { opts: { webappCustomLogoSrc: string }; photo_auto_delete_days: number } } | void>;

export async function syncMultiAccounts({
  multiAccounts,
  currentDate = new Date(),
  fetchEventData = findEvent,
  lastUpdateDateStorage = initLastUpdateDateStorage(),
}: {
  multiAccounts: MultiAccountLogic;
  currentDate?: Date;
  fetchEventData?: fetchEventDataType;
  lastUpdateDateStorage?: ReturnType<typeof initLastUpdateDateStorage>;
}) {
  const callOnceADay = initThrottleByDays({
    lastUpdateDateStorage,
    currentDate,
    daysInterval: 1,
  });
  await callOnceADay(async () => {
    await updateManager({
      updateAccounts: async (accounts) => updateAccountData(fetchEventData, accounts),
      removeAccounts: (accounts) => accounts.filter((account) => !isAccountExpired(account, currentDate)),
      multiAccounts,
    });
  });
}

export async function updateManager({
  updateAccounts,
  removeAccounts,
  multiAccounts,
}: {
  updateAccounts: (accounts: Account[]) => Promise<Account[]> | Account[];
  removeAccounts: (accounts: Account[]) => Promise<Account[]> | Account[];
  multiAccounts: MultiAccountLogic;
}) {
  let updatedAccounts = multiAccounts.getList();

  updatedAccounts = await updateAccounts(updatedAccounts);

  updatedAccounts = await removeAccounts(updatedAccounts);

  multiAccounts.setCurrentAccount(getIndex(multiAccounts.getCurrentAccount(), updatedAccounts));
  multiAccounts.setList(updatedAccounts);

  function getIndex(currentAccount: Account | null, accounts: Account[]) {
    if (accounts.length === 0 || !currentAccount) return -1;
    const foundIndex = accounts.findIndex((account) => account.attendeeUid === currentAccount.attendeeUid);
    return foundIndex > -1 ? foundIndex : 0;
  }
}

export function isAccountExpired(account: Account, currentDate: Date) {
  if (!account.expiredDate) return false;

  const current = stripTime(currentDate);
  const expired = stripTime(account.expiredDate);

  return expired <= current;

  function stripTime(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }
}

export async function updateAccountData(fetchEventData: fetchEventDataType, accounts: Account[]) {
  const eventDataMap = await prepareEventData(fetchEventData, uniq(accounts.map((account) => account.eventCode)));

  return accounts.map((account) => {
    const event = eventDataMap.get(account.eventCode);
    if (!event) return account;
    return {
      ...account,
      eventLogo: event.opts.webappCustomLogoSrc,
      expiredDate: event.photo_auto_delete_days ? addDays(account.regDate, event.photo_auto_delete_days) : null,
    };
  });
}

async function prepareEventData(fetchEventData: fetchEventDataType, eventCodes: string[]) {
  const eventDataMap = new Map();
  await Promise.allSettled(
    eventCodes.map(async (eventName) => {
      try {
        const response = await fetchEventData({ eventName });
        if (response?.result) {
          const event = response.result;
          eventDataMap.set(eventName, event);
        }
      } catch (error) {
        console.error(`Failed to update data for event ${eventName}:`, error);
      }
    })
  );
  return eventDataMap;
}
